import { InfoCircleOutlined } from '@ant-design/icons';
import { Card, Col, Row, Space, Tabs, Tooltip, Typography } from 'antd';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useMemo } from 'react';
import { LeadInterestReason, LeadsBySource, SourceMonthlyTrend } from '../../../types/dashboard';
import { glassCardStyle, MONTH_NAMES } from '../utils';

const { Text } = Typography;

interface SourceAnalysisCardsProps {
  leadsBySource: LeadsBySource[];
  sourceMonthlyTrends: SourceMonthlyTrend[];
  leadsByInterestReason?: LeadInterestReason[];
  loading: boolean;
}

// Cohesive modern brand-aligned palette moved outside component as a static constant
const BRAND_COLORS = ['#7BAA18', '#1890ff', '#faad14', '#ff4d4f', '#13c2c2', '#722ed1', '#eb2f96'];

export const SourceAnalysisCards: React.FC<SourceAnalysisCardsProps> = ({
  leadsBySource,
  sourceMonthlyTrends,
  leadsByInterestReason,
  loading
}) => {
  // Donut/Pie Chart (Disabled internal title to use Card Title instead)
  const sourcePieOptions = useMemo(() => {
    if (!leadsBySource || leadsBySource.length === 0) return {};

    const chartData = leadsBySource.map(item => ({
      name: item.source_name,
      y: item.count,
      percentage: item.percentage
    }));

    return {
      chart: {
        type: 'pie',
        height: 300,
        backgroundColor: 'transparent',
        style: { fontFamily: 'inherit' }
      },
      title: {
        text: undefined
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e2e8f0',
        shadow: true,
        pointFormat: '{series.name}: <span style="font-weight: bold; color: #0f172a;">{point.y} Leads ({point.percentage:.1f}%)</span>'
      },
      accessibility: {
        point: { valueDescriptionFormat: '{index}. {x} has {y} leads.' }
      },
      plotOptions: {
        pie: {
          innerSize: '55%', // Perfect Donut style
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.percentage:.1f}%',
            distance: 12,
            style: { fontSize: '11px', color: '#475569', textOutline: 'none' }
          },
          showInLegend: true
        }
      },
      colors: BRAND_COLORS,
      credits: { enabled: false },
      series: [{
        name: 'Jumlah Leads',
        colorByPoint: true,
        data: chartData
      }]
    };
  }, [leadsBySource]);

  // Multi-line Spline Chart (Disabled internal title to use Card Title instead)
  const sourceTrendOptions = useMemo(() => {
    if (!sourceMonthlyTrends || sourceMonthlyTrends.length === 0) return {};

    const sourcesSet = new Set<string>();
    const sourceNamesMap: Record<string, string> = {};

    sourceMonthlyTrends.forEach(item => {
      sourcesSet.add(item.source_id);
      sourceNamesMap[item.source_id] = item.source_name;
    });

    const uniqueSourceIds = Array.from(sourcesSet);
    const categories = MONTH_NAMES;

    const series = uniqueSourceIds.map(sourceId => {
      const data = Array.from({ length: 12 }, (_, monthIdx) => {
        const m = monthIdx + 1;
        const match = sourceMonthlyTrends?.find(t => t.source_id === sourceId && t.month === m);
        return match ? match.count : 0;
      });

      return {
        name: sourceNamesMap[sourceId],
        data
      };
    });

    return {
      chart: {
        type: 'spline', // Modern smooth curves
        height: 300,
        backgroundColor: 'transparent',
        style: { fontFamily: 'inherit' }
      },
      title: {
        text: undefined
      },
      xAxis: {
        categories,
        crosshair: true,
        labels: { style: { color: '#64748b' } },
        lineColor: '#cbd5e1'
      },
      yAxis: {
        min: 0,
        title: { text: 'Jumlah Leads', style: { color: '#64748b' } },
        labels: { style: { color: '#64748b' } },
        gridLineColor: 'rgba(0, 0, 0, 0.04)',
        gridLineDashStyle: 'Dash'
      },
      tooltip: {
        shared: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e2e8f0',
        shadow: true,
        pointFormat: '<span style="color:{series.color}">\u25CF</span> {series.name}: <span style="font-weight: bold; color: #0f172a;">{point.y} Leads</span><br/>'
      },
      plotOptions: {
        spline: {
          lineWidth: 2.5,
          marker: {
            enabled: true,
            radius: 3.5,
            symbol: 'circle'
          },
          dataLabels: { enabled: false },
          enableMouseTracking: true
        }
      },
      colors: BRAND_COLORS,
      credits: { enabled: false },
      series
    };
  }, [sourceMonthlyTrends]);

  // Horizontal Bar Chart for Reasons (Disabled internal title to use Card Title instead)
  const reasonsChartOptions = useMemo(() => {
    if (!leadsByInterestReason || leadsByInterestReason.length === 0) return {};

    // Get unique reason names as categories
    const uniqueReasons = Array.from(new Set(leadsByInterestReason.map(item => item.reason_name)));

    // Get unique interest statuses as series
    const uniqueStatuses = Array.from(new Set(leadsByInterestReason.map(item => item.interest_status)));

    const series = uniqueStatuses.map(status => {
      const data = uniqueReasons.map(reasonName => {
        const match = leadsByInterestReason.find(
          item => item.interest_status === status && item.reason_name === reasonName
        );
        return match ? match.count : 0;
      });

      let displayName = status;
      let color: string | undefined = undefined;

      if (status === 'tidak_minat') {
        displayName = 'Gagal (Tidak Minat)';
        color = '#ff4d4f'; // Red color for "Tidak Minat"
      } else if (status === 'minat_proses') {
        displayName = 'Minat (Proses)';
      } else if (status === 'minat_pesan') {
        displayName = 'Minat (Pesan)';
      } else if (status === 'minat_tidak_pesan') {
        displayName = 'Minat (Tidak Pesan)';
      } else {
        displayName = status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      }

      return {
        name: displayName,
        data,
        color
      };
    });

    return {
      chart: {
        type: 'bar',
        height: 300,
        backgroundColor: 'transparent',
        style: { fontFamily: 'inherit' }
      },
      title: {
        text: undefined
      },
      xAxis: {
        categories: uniqueReasons,
        title: { text: null },
        labels: { style: { color: '#64748b', fontSize: '10px' } },
        lineColor: '#cbd5e1'
      },
      yAxis: {
        min: 0,
        title: { text: 'Jumlah Leads', align: 'high', style: { color: '#64748b' } },
        labels: { overflow: 'justify', style: { color: '#64748b' } },
        gridLineColor: 'rgba(0, 0, 0, 0.04)',
        gridLineDashStyle: 'Dash'
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e2e8f0',
        shadow: true,
        pointFormat: '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y} Leads</b>'
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          dataLabels: {
            enabled: true,
            style: { fontSize: '10px', fontWeight: 'bold', textOutline: 'none' }
          }
        }
      },
      colors: BRAND_COLORS,
      credits: { enabled: false },
      series
    };
  }, [leadsByInterestReason]);

  return (
    <Row gutter={[20, 20]} style={{ marginBottom: '24px' }}>
      {/* Column A: Media Sources (Donut & Trend Spline inside Tabs) */}
      <Col xs={24} md={12}>
        <Card
          title={
            <Space size={6}>
              <Text strong>Analisis Media Promosi</Text>
            </Space>
          }
          bordered={false}
          style={glassCardStyle}
          bodyStyle={{ padding: '16px 20px' }}
          className="glass-card-interactive"
          loading={loading}
          extra={
            <Tooltip title="Gunakan tab untuk beralih antara Kontribusi Sebaran Media Promosi (Donut Chart) dan Tren Perolehan Leads Bulanan per Media (Spline Chart).">
              <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
            </Tooltip>
          }
        >
          <Tabs
            defaultActiveKey="share"
            type="line"
            items={[
              {
                key: 'share',
                label: 'Kontribusi Media',
                children: <HighchartsReact highcharts={Highcharts} options={sourcePieOptions} />
              },
              {
                key: 'trend',
                label: 'Tren Bulanan Media',
                children: <HighchartsReact highcharts={Highcharts} options={sourceTrendOptions} />
              }
            ]}
          />
        </Card>
      </Col>

      {/* Column B: Reason Distribution (Grouped Bar Chart) */}
      <Col xs={24} md={12}>
        <Card
          title={
            <Space size={6}>
              <Text strong>Alasan Ketertarikan & Penolakan</Text>
            </Space>
          }
          bordered={false}
          style={glassCardStyle}
          bodyStyle={{ padding: '16px 20px' }}
          className="glass-card-interactive"
          loading={loading}
          extra={
            <Tooltip title="Diagram batang horizontal ini memetakan sebaran alasan berdasarkan status ketertarikan calon pembeli (Gagal/Tidak Minat, Minat Proses, Minat Pesan, dsb). Sangat berguna bagi manajer kawasan untuk memetakan hambatan penjualan per wilayah.">
              <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
            </Tooltip>
          }
        >
          {leadsByInterestReason && leadsByInterestReason.length > 0 ? (
            <HighchartsReact highcharts={Highcharts} options={reasonsChartOptions} />
          ) : (
            <div style={{ textAlign: 'center', padding: '100px 0', color: '#64748b' }}>
              Tidak ada data analisis alasan ketertarikan untuk periode ini.
            </div>
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default SourceAnalysisCards;
